/**
 * LoadingPulse — phase-aware loading animations.
 *
 * Rationale:
 *   The user shouldn't have to read raw "thinking" text to know what
 *   the agent is doing. Different work calls for different visuals:
 *     • thinking  — pre-tool deliberation, planning, clarification
 *     • reading   — read_workbook (scan grid)
 *     • writing   — cell_op / sheet_op / format_op / structure_op /
 *                   data_op / annotate_op (cells fill in)
 *     • creating  — visual_op (chart bars rise)
 *     • searching — web_op (orbit / globe)
 *     • computing — analysis_op / compute_stats (formula spinning)
 *
 * Each variant reuses the same right-side AI core so the brand feel
 * stays unified; the left side changes per phase to communicate the
 * actual task.
 *
 * Tech: pure SVG + CSS keyframes (defined in styles/globals.css). No
 * framer-motion, no extra deps. Animations are GPU-friendly transforms
 * and opacity changes; loops are 2.4s–3s, calm and unobtrusive.
 */

import type { LoadingPhase } from '@/stores/chatStore';
import logoUrl from '@/assets/logo.png';

interface Props {
  phase: LoadingPhase;
}

// phaseLabel returns an accessible label so screen readers can
// announce what the animation represents. Visual users see only the
// SVG — no text ships in the DOM body.
function phaseLabel(phase: LoadingPhase): string {
  switch (phase) {
    case 'reading':   return 'Excel Autopilot is reading the workbook';
    case 'writing':   return 'Excel Autopilot is writing changes';
    case 'creating':  return 'Excel Autopilot is building visuals';
    case 'searching': return 'Excel Autopilot is searching the web';
    case 'computing': return 'Excel Autopilot is running analysis';
    case 'thinking':
    default:          return 'Excel Autopilot is thinking';
  }
}

// renderCore is the shared right-side brand mark. Animated ripples
// stay (signal "active") but the symbol avatar is now the Ira Skills
// logo image instead of a generic green dot — brand consistency with
// the header.
function renderCore() {
  return (
    <g>
      <circle className="ira-ripple ira-ripple-0" cx="110" cy="50" r="14" stroke="#10B981" strokeOpacity="0.4" strokeWidth="1" fill="none" />
      <circle className="ira-ripple ira-ripple-1" cx="110" cy="50" r="14" stroke="#3B82F6" strokeOpacity="0.4" strokeWidth="1" fill="none" />
      <image href={logoUrl} x="92" y="38" width="36" height="24" preserveAspectRatio="xMidYMid meet" />
    </g>
  );
}

// renderDefs once per render — the gradient + glow filter are reused
// across every phase variant.
function renderDefs() {
  return (
    <defs>
      <radialGradient id="ira-core" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#34D399" stopOpacity="0.95" />
        <stop offset="60%" stopColor="#10B981" stopOpacity="0.55" />
        <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.0" />
      </radialGradient>
      <filter id="ira-glow" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="2.2" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
  );
}

// renderReading — magnifying glass sweeps across a 3x3 grid. The lens
// arcs left → right; rows highlight as the lens passes over them.
function renderReading() {
  return (
    <>
      {/* Grid frame */}
      <g stroke="#CBD5E1" strokeWidth="1" fill="none">
        <rect x="10" y="20" width="60" height="60" rx="3" />
        <line x1="30" y1="20" x2="30" y2="80" />
        <line x1="50" y1="20" x2="50" y2="80" />
        <line x1="10" y1="40" x2="70" y2="40" />
        <line x1="10" y1="60" x2="70" y2="60" />
      </g>
      {/* Cells light up in a left-to-right wave */}
      <g>
        <rect className="ira-scan-cell ira-scan-cell-0" x="11" y="21" width="18" height="18" rx="2" fill="#10B981" />
        <rect className="ira-scan-cell ira-scan-cell-1" x="11" y="41" width="18" height="18" rx="2" fill="#10B981" />
        <rect className="ira-scan-cell ira-scan-cell-2" x="11" y="61" width="18" height="18" rx="2" fill="#10B981" />
        <rect className="ira-scan-cell ira-scan-cell-3" x="31" y="21" width="18" height="18" rx="2" fill="#3B82F6" />
        <rect className="ira-scan-cell ira-scan-cell-4" x="31" y="41" width="18" height="18" rx="2" fill="#3B82F6" />
        <rect className="ira-scan-cell ira-scan-cell-5" x="31" y="61" width="18" height="18" rx="2" fill="#3B82F6" />
        <rect className="ira-scan-cell ira-scan-cell-6" x="51" y="21" width="18" height="18" rx="2" fill="#10B981" />
        <rect className="ira-scan-cell ira-scan-cell-7" x="51" y="41" width="18" height="18" rx="2" fill="#10B981" />
        <rect className="ira-scan-cell ira-scan-cell-8" x="51" y="61" width="18" height="18" rx="2" fill="#10B981" />
      </g>
      {/* Magnifying lens — sweeps left → right above the grid */}
      <g className="ira-lens">
        <circle cx="0" cy="0" r="9" fill="none" stroke="#10B981" strokeWidth="1.5" />
        <line x1="6" y1="6" x2="11" y2="11" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" />
      </g>
      {/* Data flow lines into core */}
      <g stroke="#94A3B8" strokeWidth="1" fill="none" strokeLinecap="round">
        <line className="ira-flow ira-flow-0" x1="70" y1="30" x2="108" y2="50" strokeDasharray="3 4" />
        <line className="ira-flow ira-flow-1" x1="70" y1="50" x2="108" y2="50" strokeDasharray="3 4" />
        <line className="ira-flow ira-flow-2" x1="70" y1="70" x2="108" y2="50" strokeDasharray="3 4" />
      </g>
    </>
  );
}

// renderWriting — cells filling in sequence, like a pen filling out
// a form row by row. Each cell pops into existence with a small scale
// bounce, then settles.
function renderWriting() {
  return (
    <>
      <g stroke="#CBD5E1" strokeWidth="1" fill="none">
        <rect x="10" y="20" width="60" height="60" rx="3" />
        <line x1="30" y1="20" x2="30" y2="80" />
        <line x1="50" y1="20" x2="50" y2="80" />
        <line x1="10" y1="40" x2="70" y2="40" />
        <line x1="10" y1="60" x2="70" y2="60" />
      </g>
      {/* Cells appear sequentially row-major; mimics writing data row by row */}
      <g>
        <rect className="ira-fill-cell ira-fill-cell-0" x="11" y="21" width="18" height="18" rx="2" fill="#10B981" />
        <rect className="ira-fill-cell ira-fill-cell-1" x="31" y="21" width="18" height="18" rx="2" fill="#10B981" />
        <rect className="ira-fill-cell ira-fill-cell-2" x="51" y="21" width="18" height="18" rx="2" fill="#10B981" />
        <rect className="ira-fill-cell ira-fill-cell-3" x="11" y="41" width="18" height="18" rx="2" fill="#3B82F6" />
        <rect className="ira-fill-cell ira-fill-cell-4" x="31" y="41" width="18" height="18" rx="2" fill="#3B82F6" />
        <rect className="ira-fill-cell ira-fill-cell-5" x="51" y="41" width="18" height="18" rx="2" fill="#3B82F6" />
        <rect className="ira-fill-cell ira-fill-cell-6" x="11" y="61" width="18" height="18" rx="2" fill="#10B981" />
        <rect className="ira-fill-cell ira-fill-cell-7" x="31" y="61" width="18" height="18" rx="2" fill="#10B981" />
        <rect className="ira-fill-cell ira-fill-cell-8" x="51" y="61" width="18" height="18" rx="2" fill="#10B981" />
      </g>
      <g stroke="#94A3B8" strokeWidth="1" fill="none" strokeLinecap="round">
        <line className="ira-flow ira-flow-0" x1="108" y1="50" x2="70" y2="30" strokeDasharray="3 4" />
        <line className="ira-flow ira-flow-1" x1="108" y1="50" x2="70" y2="50" strokeDasharray="3 4" />
        <line className="ira-flow ira-flow-2" x1="108" y1="50" x2="70" y2="70" strokeDasharray="3 4" />
      </g>
    </>
  );
}

// renderCreating — bar chart bars rise from a baseline. Communicates
// "building visual" without being a literal screenshot of a chart.
function renderCreating() {
  return (
    <>
      {/* Chart axes */}
      <g stroke="#CBD5E1" strokeWidth="1" strokeLinecap="round" fill="none">
        <line x1="14" y1="80" x2="74" y2="80" />
        <line x1="14" y1="20" x2="14" y2="80" />
      </g>
      {/* Bars — stretch upward from the axis */}
      <g>
        <rect className="ira-bar ira-bar-0" x="20" y="20" width="9" height="60" rx="1.5" fill="#10B981" />
        <rect className="ira-bar ira-bar-1" x="33" y="20" width="9" height="60" rx="1.5" fill="#3B82F6" />
        <rect className="ira-bar ira-bar-2" x="46" y="20" width="9" height="60" rx="1.5" fill="#10B981" />
        <rect className="ira-bar ira-bar-3" x="59" y="20" width="9" height="60" rx="1.5" fill="#3B82F6" />
      </g>
      {/* Trend line over bars */}
      <polyline
        className="ira-trend"
        points="24,60 38,40 51,52 64,30"
        stroke="#0EA5E9"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <g stroke="#94A3B8" strokeWidth="1" fill="none" strokeLinecap="round">
        <line className="ira-flow ira-flow-0" x1="74" y1="50" x2="108" y2="50" strokeDasharray="3 4" />
      </g>
    </>
  );
}

// renderSearching — concentric orbits around a globe. Communicates
// "looking outside the workbook" without literally drawing a globe icon
// (which gets pixel-fuzzy at this size).
function renderSearching() {
  return (
    <>
      {/* Globe meridians */}
      <g stroke="#CBD5E1" strokeWidth="1" fill="none">
        <circle cx="40" cy="50" r="22" />
        <ellipse cx="40" cy="50" rx="22" ry="9" />
        <ellipse cx="40" cy="50" rx="9" ry="22" />
      </g>
      {/* Two orbiting dots — different speeds, different colors */}
      <circle className="ira-orbit ira-orbit-0" cx="62" cy="50" r="3" fill="#10B981" />
      <circle className="ira-orbit ira-orbit-1" cx="18" cy="50" r="2.5" fill="#3B82F6" />
      <g stroke="#94A3B8" strokeWidth="1" fill="none" strokeLinecap="round">
        <line className="ira-flow ira-flow-0" x1="64" y1="50" x2="108" y2="50" strokeDasharray="3 4" />
      </g>
    </>
  );
}

// renderComputing — Σ symbol with shimmering numeric particles
// rotating around it. Communicates "calculation under way" without
// printing actual numbers.
function renderComputing() {
  return (
    <>
      {/* Sigma frame */}
      <text
        x="40"
        y="62"
        fontSize="46"
        fontWeight="600"
        textAnchor="middle"
        fill="#10B981"
        fontFamily="Georgia, serif"
        className="ira-sigma"
      >
        Σ
      </text>
      {/* Particles orbiting the sigma */}
      <circle className="ira-particle ira-particle-0" cx="40" cy="20" r="2" fill="#3B82F6" />
      <circle className="ira-particle ira-particle-1" cx="65" cy="50" r="2" fill="#10B981" />
      <circle className="ira-particle ira-particle-2" cx="40" cy="80" r="2" fill="#3B82F6" />
      <circle className="ira-particle ira-particle-3" cx="15" cy="50" r="2" fill="#10B981" />
      <g stroke="#94A3B8" strokeWidth="1" fill="none" strokeLinecap="round">
        <line className="ira-flow ira-flow-0" x1="68" y1="50" x2="108" y2="50" strokeDasharray="3 4" />
      </g>
    </>
  );
}

// renderThinking — original 3x3 grid pulse + flow lines. Used as the
// fallback for ask_clarification, idle thinking, and unknown phases.
function renderThinking() {
  return (
    <>
      <g stroke="#CBD5E1" strokeWidth="1" fill="none">
        <rect x="10" y="20" width="60" height="60" rx="3" />
        <line x1="30" y1="20" x2="30" y2="80" />
        <line x1="50" y1="20" x2="50" y2="80" />
        <line x1="10" y1="40" x2="70" y2="40" />
        <line x1="10" y1="60" x2="70" y2="60" />
      </g>
      <g>
        <rect className="ira-cell ira-cell-0" x="11" y="21" width="18" height="18" rx="2" fill="#10B981" />
        <rect className="ira-cell ira-cell-1" x="31" y="21" width="18" height="18" rx="2" fill="#3B82F6" />
        <rect className="ira-cell ira-cell-2" x="51" y="21" width="18" height="18" rx="2" fill="#10B981" />
        <rect className="ira-cell ira-cell-3" x="11" y="41" width="18" height="18" rx="2" fill="#3B82F6" />
        <rect className="ira-cell ira-cell-4" x="31" y="41" width="18" height="18" rx="2" fill="#10B981" />
        <rect className="ira-cell ira-cell-5" x="51" y="41" width="18" height="18" rx="2" fill="#3B82F6" />
        <rect className="ira-cell ira-cell-6" x="11" y="61" width="18" height="18" rx="2" fill="#10B981" />
        <rect className="ira-cell ira-cell-7" x="31" y="61" width="18" height="18" rx="2" fill="#3B82F6" />
        <rect className="ira-cell ira-cell-8" x="51" y="61" width="18" height="18" rx="2" fill="#10B981" />
      </g>
      <g stroke="#94A3B8" strokeWidth="1" fill="none" strokeLinecap="round">
        <line className="ira-flow ira-flow-0" x1="70" y1="30" x2="108" y2="50" strokeDasharray="3 4" />
        <line className="ira-flow ira-flow-1" x1="70" y1="50" x2="108" y2="50" strokeDasharray="3 4" />
        <line className="ira-flow ira-flow-2" x1="70" y1="70" x2="108" y2="50" strokeDasharray="3 4" />
      </g>
    </>
  );
}

// renderPhaseBody picks the correct left-half SVG body. Right-side core
// + defs are the same on every phase.
function renderPhaseBody(phase: LoadingPhase) {
  switch (phase) {
    case 'reading':   return renderReading();
    case 'writing':   return renderWriting();
    case 'creating':  return renderCreating();
    case 'searching': return renderSearching();
    case 'computing': return renderComputing();
    case 'thinking':
    default:          return renderThinking();
  }
}

export function LoadingPulse({ phase }: Props) {
  return (
    <div className="flex items-center gap-3 px-1 py-2 select-none">
      <div className="ira-loading-pulse" aria-label={phaseLabel(phase)} role="status">
        <svg
          width="56"
          height="40"
          viewBox="0 0 140 100"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          {renderDefs()}
          {renderPhaseBody(phase)}
          {renderCore()}
        </svg>
      </div>
    </div>
  );
}
